import {
  calcDutyHours,
  DEFAULT_CONTRACT_DETAILS,
  DRIVER_PROFILES,
  normalizeContractDetails,
} from './mockDriverErpData';

const UAE_WEEKEND_DAYS = [0, 6];
export const FLAT_TRIP_ALLOWANCE_AED = 75;

export const PENALTY_RATES = {
  geofence_breach: { label: 'Geofence Breach', amount: 100 },
  ai_camera_alert: { label: 'AI Fatigue Camera Alert', amount: 50 },
};

export const PAYROLL_COMPANY = {
  name: 'OGTS LogiCore™ Transport Services LLC',
  tradeLicense: 'TL-784521-OGTS',
  address: 'Jebel Ali Free Zone, Dubai, UAE',
  hrContact: 'payroll@ogts-logicore.ae',
};

const ROUTES = [
  'Jebel Ali Terminal → Ruwais Refinery',
  'Ruwais Depot → Fujairah Port',
  'DIC Depot → Jebel Ali Free Zone',
  'Hamriyah → Musaffah',
  'Fujairah → Dubai Silicon Oasis',
];

const CARGO_TYPES = ['Crude Oil', 'LPG', 'Chemical ADR', 'Diesel ULSD', 'Bitumen 80/100'];

function hashSeed(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function buildDeliveryOrdersMaster() {
  const orders = [];
  DRIVER_PROFILES.forEach((driver) => {
    const tripCount = (hashSeed(`${driver.id}-drops`) % 14) + 4;
    for (let i = 0; i < tripCount; i += 1) {
      const day = (hashSeed(`${driver.id}-day-${i}`) % 28) + 1;
      const dateKey = `2026-06-${String(day).padStart(2, '0')}`;
      orders.push({
        id: `DO-${driver.id}-${String(i + 1).padStart(3, '0')}`,
        driverId: driver.id,
        driverName: driver.name,
        status: 'Delivered',
        deliveredAt: dateKey,
        route: ROUTES[hashSeed(`${driver.id}-route-${i}`) % ROUTES.length],
        distanceKm: 40 + (hashSeed(`${driver.id}-km-${i}`) % 260),
        cargoType: CARGO_TYPES[hashSeed(`${driver.id}-cargo-${i}`) % CARGO_TYPES.length],
      });
    }
  });
  return orders;
}

function buildComplianceMaster(punches) {
  const hits = [];
  const seen = new Set();

  punches.forEach((p) => {
    if (p.geofenceStatus !== 'failed') return;
    const dateKey = toDateKey(new Date(p.timestamp));
    const key = `${p.driverId}-${dateKey}-geo`;
    if (seen.has(key)) return;
    seen.add(key);
    hits.push({
      id: `FC-${key}`,
      driverId: p.driverId,
      type: 'geofence_breach',
      label: PENALTY_RATES.geofence_breach.label,
      dateKey,
      amount: PENALTY_RATES.geofence_breach.amount,
      source: 'Fleet Guard Engine',
    });
  });

  DRIVER_PROFILES.forEach((driver) => {
    for (let day = 1; day <= 28; day += 1) {
      const dateKey = `2026-06-${String(day).padStart(2, '0')}`;
      if (hashSeed(`${driver.id}-ai-${dateKey}`) % 21 !== 0) continue;
      hits.push({
        id: `FC-${driver.id}-${dateKey}-ai`,
        driverId: driver.id,
        type: 'ai_camera_alert',
        label: PENALTY_RATES.ai_camera_alert.label,
        dateKey,
        amount: PENALTY_RATES.ai_camera_alert.amount,
        source: 'Fleet Guard Engine',
      });
    }
  });

  return hits;
}

export const DELIVERY_ORDERS = buildDeliveryOrdersMaster();

export function formatPayrollMonth(year, month) {
  return new Intl.DateTimeFormat('en-AE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Dubai',
  }).format(new Date(year, month, 1));
}

export function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isInPayrollMonth(dateKey, year, month) {
  if (!dateKey) return false;
  const [y, m] = dateKey.split('-').map(Number);
  return y === year && m === month + 1;
}

export function isWeekend(date) {
  return UAE_WEEKEND_DAYS.includes(date.getDay());
}

export function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay();
  const days = [];

  for (let i = 0; i < startPad; i += 1) {
    days.push(null);
  }

  for (let d = 1; d <= last.getDate(); d += 1) {
    days.push(new Date(year, month, d));
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
}

function simulateDayFallback(driverId, dateKey) {
  const seed = hashSeed(`${driverId}-${dateKey}`);
  const breach = seed % 17 === 0;
  const absent = seed % 11 === 0;
  const present = !breach && !absent;
  const otHours = present ? Math.round(((seed % 40) / 10) * 10) / 10 : 0;

  if (breach) return { status: 'breach', otHours: 0, verified: false, source: 'simulated' };
  if (absent) return { status: 'absent', otHours: 0, verified: false, source: 'simulated' };
  return { status: 'present', otHours, verified: false, source: 'simulated' };
}

function deriveDayFromPunches(punches) {
  const successful = punches.filter((p) => p.geofenceStatus === 'success');
  const failed = punches.filter((p) => p.geofenceStatus === 'failed');

  if (successful.length === 0 && failed.length > 0) {
    return { status: 'breach', otHours: 0, verified: false, source: 'punch' };
  }
  if (successful.length === 0) {
    return { status: 'absent', otHours: 0, verified: false, source: 'punch' };
  }

  const sorted = [...successful].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );

  let closedHours = 0;
  let openStart = null;

  sorted.forEach((event) => {
    if (event.punchType === 'PUNCH_IN') openStart = event.timestamp;
    else if (event.punchType === 'PUNCH_OUT' && openStart) {
      closedHours += calcDutyHours(openStart, event.timestamp);
      openStart = null;
    }
  });

  const otHours = Math.max(0, Math.round((closedHours - 8) * 10) / 10);

  return {
    status: 'present',
    otHours,
    dutyHours: Math.round(closedHours * 10) / 10,
    verified: true,
    source: 'punch',
  };
}

export function buildDriverMonthDays(driverId, punches, year, month) {
  const last = new Date(year, month + 1, 0).getDate();
  const days = [];

  for (let d = 1; d <= last; d += 1) {
    const date = new Date(year, month, d);
    const dateKey = toDateKey(date);

    if (isWeekend(date)) {
      days.push({
        date,
        dateKey,
        day: d,
        status: 'weekend',
        otHours: 0,
        penalties: [],
        hasPenalty: false,
        hasOvertime: false,
        verified: false,
      });
      continue;
    }

    const dayPunches = punches.filter((p) => {
      if (p.driverId !== driverId) return false;
      return toDateKey(new Date(p.timestamp)) === dateKey;
    });

    const attendance =
      dayPunches.length > 0
        ? deriveDayFromPunches(dayPunches)
        : simulateDayFallback(driverId, dateKey);

    days.push({
      date,
      dateKey,
      day: d,
      ...attendance,
      penalties: [],
      hasPenalty: false,
      hasOvertime: (attendance.otHours || 0) > 0 && attendance.status === 'present',
    });
  }

  return days;
}

/** OT rate = (basicSalary / 200) × 1.5 per user spec */
export function calcContractOvertimeRate(baseSalary) {
  return Math.round((baseSalary / 200) * 1.5 * 100) / 100;
}

export function computeAutomatedPayroll(driver, punches, timesheets, year, month) {
  const contract = normalizeContractDetails(
    driver?.contractDetails || DEFAULT_CONTRACT_DETAILS,
  );

  const baseSalary = Number(contract.basicSalary) || 0;

  const deliveredTrips = DELIVERY_ORDERS.filter(
    (order) =>
      order.driverId === driver.id &&
      order.status === 'Delivered' &&
      isInPayrollMonth(order.deliveredAt, year, month),
  );

  const tripCount = deliveredTrips.length;
  const tripIncentive = tripCount * FLAT_TRIP_ALLOWANCE_AED;

  const monthDays = buildDriverMonthDays(driver.id, punches, year, month);

  let totalOvertimeHours = monthDays
    .filter((d) => d.status === 'present')
    .reduce((sum, d) => sum + (d.otHours || 0), 0);

  const timesheetRow = timesheets?.find((t) => t.driverId === driver.id);
  if (timesheetRow?.overtime > totalOvertimeHours) {
    totalOvertimeHours = timesheetRow.overtime;
  }

  totalOvertimeHours = Math.round(totalOvertimeHours * 10) / 10;

  const overtimeRate = calcContractOvertimeRate(baseSalary);
  const overtimePay = Math.round(totalOvertimeHours * overtimeRate);

  const activeDays = monthDays.filter((d) => d.status === 'present').length;
  const foodRate = contract.foodAllowanceAmount;
  const foodAllowance =
    contract.foodAllowanceType === 'Per Duty Day'
      ? activeDays * foodRate
      : foodRate;

  const complianceHits = buildComplianceMaster(punches).filter(
    (hit) =>
      hit.driverId === driver.id && isInPayrollMonth(hit.dateKey, year, month),
  );

  complianceHits.forEach((hit) => {
    const day = monthDays.find((d) => d.dateKey === hit.dateKey);
    if (!day) return;
    day.penalties.push(hit);
    day.hasPenalty = true;
  });

  const penaltyDeductions = complianceHits.reduce((sum, hit) => sum + hit.amount, 0);
  const grossEarnings = baseSalary + tripIncentive + overtimePay + foodAllowance;
  const netPayable = grossEarnings - penaltyDeductions;

  return {
    contract,
    monthDays,
    monthLabel: formatPayrollMonth(year, month),
    year,
    month,
    operational: {
      deliveredTrips,
      complianceHits,
      timesheetRow,
    },
    stats: {
      tripCount,
      totalOvertimeHours,
      penaltyHits: complianceHits.length,
      presentDays: monthDays.filter((d) => d.status === 'present').length,
      weekendDays: monthDays.filter((d) => d.status === 'weekend').length,
      absentDays: monthDays.filter((d) => d.status === 'absent').length,
      breachDays: monthDays.filter((d) => d.status === 'breach').length,
    },
    computed: {
      baseSalary,
      tripCount,
      tripIncentive,
      totalOvertimeHours,
      overtimeRate,
      overtimePay,
      activeDays,
      foodRate,
      foodAllowance,
      grossEarnings,
      penaltyDeductions,
      netPayable: Math.round(netPayable * 100) / 100,
    },
  };
}

/** @deprecated use computeAutomatedPayroll */
export function buildPayrollSummary(driver, punches, year, month, timesheets = []) {
  const result = computeAutomatedPayroll(driver, punches, timesheets, year, month);
  const { computed, stats, operational } = result;

  return {
    ...result,
    breakdown: {
      basicSalary: computed.baseSalary,
      tripIncentives: computed.tripIncentive,
      tripIncentivesLabel: `${computed.tripCount} Approved Drops × ${FLAT_TRIP_ALLOWANCE_AED} AED`,
      overtimePayout: computed.overtimePay,
      overtimeLabel: `${computed.totalOvertimeHours} hrs × ${computed.overtimeRate} AED/hr`,
      otRate: computed.overtimeRate,
      penaltiesTotal: computed.penaltyDeductions,
      penaltyItems: operational.complianceHits,
      grossEarnings: computed.grossEarnings,
      netPayout: computed.netPayable,
    },
    stats: {
      ...stats,
      completedTrips: stats.tripCount,
      totalOtHours: stats.totalOvertimeHours,
      penaltyHits: stats.penaltyHits,
    },
    operational: {
      completedTrips: operational.deliveredTrips,
      penaltyLogs: operational.complianceHits,
      overtimeLogs: [],
    },
  };
}

export function formatAed(amount) {
  return `${Number(amount || 0).toLocaleString('en-AE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} AED`;
}

export function formatAedPlain(amount) {
  return Number(amount || 0).toLocaleString('en-AE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
